/**
 * Firestore error helpers — surface the precise reason an operation failed
 * so admins can act on it (deploy an index, fix rules, sign in again, etc.)
 * instead of seeing a generic "Failed to save".
 */

export function firestoreErrorCode(err: unknown): string | undefined {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

export function describeFirestoreError(err: unknown): string {
  const code = firestoreErrorCode(err);
  switch (code) {
    case "permission-denied":
      return "Firestore rules denied this operation. Verify the rules are published.";
    case "failed-precondition":
      return "Missing Firestore index. The browser console has an auto-create link.";
    case "unavailable":
      return "Firestore is temporarily unavailable. Please try again.";
    case "unauthenticated":
      return "Your session expired. Please sign in again.";
    case "not-found":
      return "Document not found.";
    case "already-exists":
      return "A document with that ID already exists.";
    case "resource-exhausted":
      return "Firestore quota exceeded. Try again later.";
    case "cancelled":
      return "Operation cancelled.";
    default:
      return code
        ? `Couldn't save (${code}). See browser console for details.`
        : "Couldn't save. See browser console for details.";
  }
}
